import React from 'react';
import { Link } from 'react-router-dom';
import {
  Crown,
  ArrowRight,
  Check,
  Clock,
  MapPin,
  Utensils,
  Gift,
  Users,
  Phone,
  Sparkles,
} from 'lucide-react';
import './HomePage.funnel.css';
import './VipDayPage.css';

const CHECKOUT_ANNUAL_URL = 'https://www.commas.com/checkout/mgmGR4XvWZfjh3bJm';
const CAL_URL = 'https://calendly.com/thecollinsacademynew/30min';

const INCLUDES = [
  {
    icon: Phone,
    title: 'Private Pre-Day Prep Call',
    body: 'A 45-minute strategy call before your VIP Day so we walk in knowing your file, your business, and your $250K target.',
  },
  {
    icon: MapPin,
    title: 'Hosted At One of the CEO\'s Residences',
    body: 'Full day at a private Collins residence — luxury environment, zero distractions. Travel and accommodation coordination handled for out-of-town clients.',
  },
  {
    icon: Crown,
    title: '8 Hours Direct With the CEO',
    body: 'One-on-one with LaMarqus Collins. No junior coach, no group room — every hour of the day is yours.',
  },
  {
    icon: Sparkles,
    title: 'Custom $250K Capital Blueprint',
    body: 'We build your credit repair, tradeline stack, entity architecture, and lender walk-in plan live — on the whiteboard, in the room.',
  },
  {
    icon: Users,
    title: 'Introductions Made In The Room',
    body: 'Direct email intros to lenders, bankers, and business partners from the CEO\'s private network — sent while you\'re still on-site.',
  },
  {
    icon: Utensils,
    title: 'Private Executive Dinner',
    body: 'Close the day with a private dinner with the CEO and a hand-picked guest from the Academy network.',
  },
  {
    icon: Gift,
    title: 'Collins Academy VIP Gift Box',
    body: 'Branded travel kit, signed hardcover copies of every Collins book, and a Collins Academy jacket to take home.',
  },
];

const AGENDA = [
  { time: '9:00 AM', label: 'Arrival, brand-tour of the residence, breakfast on-site' },
  { time: '10:00 AM', label: 'Deep-dive: credit file audit + dispute engine walk-through' },
  { time: '11:30 AM', label: 'Tradeline stack + entity structuring session' },
  { time: '12:30 PM', label: 'Working lunch with the CEO' },
  { time: '1:30 PM', label: 'Live lender-package build — you leave with an approvable file' },
  { time: '3:00 PM', label: 'AI Autopilot buildout: funnel, offer stack, follow-up flows mapped' },
  { time: '4:30 PM', label: 'Wealth Rebuild sprint calendar + accountability structure locked' },
  { time: '6:00 PM', label: 'Private executive dinner + closing debrief' },
];

const VipDayPage = () => {
  return (
    <div className="tcf vip-shell" data-testid="vip-day-page">
      {/* Hero */}
      <section className="vip-hero" data-testid="vip-hero">
        <div className="vip-hero-inner">
          <span className="vip-badge">
            <Crown size={14} /> Pay-In-Full Exclusive
          </span>
          <h1 className="vip-hero-title">
            The <span className="gold">CEO VIP Day.</span>
          </h1>
          <p className="vip-hero-sub">
            Eight hours, one-on-one with LaMarqus Collins, inside a private Collins residence.
            No group room. No junior coach. Just the CEO, your file, and the blueprint that turns
            you into a funded, structured, un-ignorable operator.
          </p>
          <div className="vip-hero-meta">
            <div className="vip-meta-item">
              <Clock size={16} />
              <span>Full 8-hour private day</span>
            </div>
            <div className="vip-meta-item">
              <MapPin size={16} />
              <span>Hosted at a private CEO residence</span>
            </div>
            <div className="vip-meta-item">
              <Sparkles size={16} />
              <span>$2,500 value · included with Pay-In-Full</span>
            </div>
          </div>
          <div className="vip-hero-ctas">
            <a
              href={CHECKOUT_ANNUAL_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="tcf-btn tcf-btn-gold tcf-btn-lg"
              data-testid="vip-claim-cta"
            >
              Claim My VIP Day — Pay In Full $10,000 <ArrowRight size={18} />
            </a>
            <a
              href={CAL_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="tcf-btn tcf-btn-secondary"
              data-testid="vip-questions-cta"
              style={{ borderColor: '#fff', color: '#fff' }}
            >
              Talk to the Team First
            </a>
          </div>
        </div>
      </section>

      {/* What's included */}
      <section className="vip-includes" data-testid="vip-includes">
        <div className="vip-section-head">
          <span className="tcf-section-eyebrow">Everything Included</span>
          <h2 className="tcf-section-title">What your VIP Day covers.</h2>
        </div>
        <div className="vip-includes-grid">
          {INCLUDES.map(({ icon: Icon, title, body }) => (
            <article
              key={title}
              className="vip-include-card"
              data-testid={`vip-include-${title.slice(0, 20).replace(/\s+/g, '-').toLowerCase()}`}
            >
              <span className="vip-include-icon">
                <Icon size={22} />
              </span>
              <h3>{title}</h3>
              <p>{body}</p>
            </article>
          ))}
        </div>
      </section>

      {/* Day agenda */}
      <section className="vip-agenda" data-testid="vip-agenda">
        <div className="vip-section-head">
          <span className="tcf-section-eyebrow" style={{ color: 'var(--tca-gold)' }}>
            The Blueprint
          </span>
          <h2 className="tcf-section-title" style={{ color: 'var(--tca-cream)' }}>
            A full day, engineered end-to-end.
          </h2>
          <p className="vip-agenda-sub">
            Every hour has a deliverable. You do not leave with a notebook full of ideas — you
            leave with a signed capital roadmap, an approvable lender file, and a scheduled
            follow-through with the Collins team.
          </p>
        </div>
        <ol className="vip-agenda-list">
          {AGENDA.map((row) => (
            <li key={row.time} data-testid={`vip-agenda-${row.time.replace(/[^0-9]/g, '')}`}>
              <span className="vip-agenda-time">{row.time}</span>
              <span className="vip-agenda-line" />
              <span className="vip-agenda-label">{row.label}</span>
            </li>
          ))}
        </ol>
      </section>

      {/* Who qualifies */}
      <section className="vip-eligible" data-testid="vip-eligible">
        <div className="vip-eligible-inner">
          <div>
            <span className="tcf-section-eyebrow">Who qualifies</span>
            <h2 className="tcf-section-title">Pay-In-Full clients only.</h2>
            <p className="vip-eligible-copy">
              The VIP Day is a $2,500-value bonus for members who enroll on the annual
              Pay-In-Full plan. Monthly plan clients are welcome to book the VIP Day separately at
              its standalone rate — but only Pay-In-Full members receive it complimentary as part
              of the Collins Stack.
            </p>
            <ul className="vip-eligible-list">
              <li><Check size={18} /> $10,000 one-time payment (Pay-In-Full plan)</li>
              <li><Check size={18} /> Booked within 90 days of enrollment</li>
              <li><Check size={18} /> Pre-Day prep call completed</li>
              <li><Check size={18} /> Non-transferable, one-time redemption per membership year</li>
            </ul>
          </div>
          <div className="vip-eligible-card">
            <span className="vip-eligible-tag">Included with</span>
            <div className="vip-eligible-plan">Pay In Full</div>
            <div className="vip-eligible-price">$10,000<span>/year</span></div>
            <div className="vip-eligible-note">One-time payment · Save $2,000 vs monthly</div>
            <a
              href={CHECKOUT_ANNUAL_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="tcf-btn tcf-btn-primary tcf-btn-lg"
              data-testid="vip-eligible-cta"
              style={{ width: '100%', justifyContent: 'center', marginTop: 20 }}
            >
              Enroll & Claim VIP Day <ArrowRight size={18} />
            </a>
            <Link
              to="/"
              className="vip-eligible-back"
              data-testid="vip-back-home"
            >
              ← Back to the full offer
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default VipDayPage;
